import React from 'react';
import PropTypes from 'prop-types';
import { FaMagic } from 'react-icons/fa';
import { Button, DataTable, RequiredMark, Select } from '../../../components/EntityList';
import { useLabels } from '../../../hooks/useLabels';
import { assignColumn, columnFallback } from './autoMap';
import MappingOptions from './MappingOptions';

export default function StepMapping({
	schema,
	headers,
	mapping,
	onMappingChange,
	onAutoMap,
	missingRequired,
	fillableUnmapped = [],
	links,
	onLinksChange,
	options,
	onOptionsChange,
}) {
	const { t } = useLabels();
	const referencesByKey = new Map(schema.references.map((reference) => [reference.key, reference]));
	const setLookup = (key, by) => onLinksChange({ ...links, [key]: { ...links[key], by, header: mapping[key] } });

	const columns = [
		{
			key: 'field',
			header: t('import.fieldColumn').replace('{entity}', t(schema.labelKey).toLowerCase()),
			render: (field) => (
				<span className='tw:font-medium tw:text-ink-0'>
					{t(field.labelKey)}
					{field.required && <RequiredMark />}
				</span>
			),
		},
		{
			key: 'column',
			header: t('import.excelColumn'),
			render: (field) => (
				<Select
					id={`map-${field.key}`}
					value={mapping[field.key] || ''}
					onChange={(e) => onMappingChange(assignColumn(mapping, field.key, e.target.value))}
					// Fillable fields are completed in the review; the rest are simply not imported
					placeholder={field.fillable ? t('import.fillLater') : t('import.ignoreColumn')}
					options={headers}
					className='tw:min-w-[200px]'
				/>
			),
		},
		{
			key: 'lookup',
			header: t('import.lookupColumn'),
			render: (field) => {
				const reference = field.type === 'reference' ? referencesByKey.get(field.reference) : null;
				if (reference && mapping[field.key]) {
					// The Excel has no ids: which field of the related record the column holds (Padrón, nombre...)
					return (
						<Select
							id={`lookup-${field.key}`}
							value={links[reference.key]?.by || reference.defaultLookup}
							onChange={(e) => setLookup(reference.key, e.target.value)}
							options={reference.lookupOptions.map((option) => ({ value: option.key, label: t(option.labelKey) }))}
							className='tw:min-w-[220px]'
						/>
					);
				}
				const fallback = columnFallback(field, mapping);
				if (fallback) {
					return <span className='tw:text-[12.5px] tw:text-ink-2'>{t(fallback).replace('{column}', mapping[field.fallbackFrom] || '')}</span>;
				}
				return null;
			},
		},
	];

	return (
		<div className='tw:space-y-4'>
			<div className='tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3'>
				<p className='tw:m-0 tw:max-w-[640px] tw:text-[13px] tw:text-ink-2'>{t('import.mappingHelp')}</p>
				<Button size='sm' icon={<FaMagic />} onClick={onAutoMap}>
					{t('import.autoMap')}
				</Button>
			</div>

			{missingRequired.length > 0 && (
				<div className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-4 tw:py-3 tw:text-[13px]'>
					{t('import.missingRequired')} <strong>{missingRequired.map((f) => t(f.labelKey)).join(', ')}</strong>
				</div>
			)}

			{fillableUnmapped.length > 0 && (
				<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-4 tw:py-3 tw:text-[13px] tw:text-ink-1'>
					{t('import.fillableHint')} <strong>{fillableUnmapped.map((f) => t(f.labelKey)).join(', ')}</strong>
				</div>
			)}

			<DataTable columns={columns} rows={schema.fields} rowKey={(field) => field.key} />

			<MappingOptions
				schema={schema}
				mapping={mapping}
				links={links}
				onLinksChange={onLinksChange}
				options={options}
				onOptionsChange={onOptionsChange}
			/>
		</div>
	);
}

StepMapping.propTypes = {
	schema: PropTypes.object.isRequired,
	headers: PropTypes.array.isRequired,
	mapping: PropTypes.object.isRequired,
	onMappingChange: PropTypes.func.isRequired,
	onAutoMap: PropTypes.func.isRequired,
	missingRequired: PropTypes.array.isRequired,
	fillableUnmapped: PropTypes.array,
	links: PropTypes.object.isRequired,
	onLinksChange: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
	onOptionsChange: PropTypes.func.isRequired,
};
